<template>
  <div
    v-if="totalPages > 1"
    :class="componentClasses"
  >
    <Buttons class="stk-pagination-buttons">
      <Button
        v-for="item in pageButtons"
        :key="item.number"
        :variant="item.variant"
        :disabled="item.disabled"
        square
        @click="emit('onPageClick', item.number)"
      >
        {{ item.label }}
      </Button>
    </Buttons>

    <Buttons>
      <Button
        icon-before="fas fa-arrow-left"
        :disabled="!hasPrevious"
        @click="emit('onPrevious')"
      >
        Previous
      </Button>
      <Button
        icon-after="fas fa-arrow-right"
        :disabled="!hasNext"
        @click="emit('onNext')"
      >
        Next Page
      </Button>
    </Buttons>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import Button from '../../form/button/Button.vue';
import Buttons from '../../form/button/Buttons.vue';

const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  hasPrevious: {
    type: Boolean,
    required: true,
  },
  hasNext: {
    type: Boolean,
    required: true,
  },
  size: {
    type: String,
  },
});

const emit = defineEmits(['onPrevious', 'onNext']);

const insertEllipsis = arr => {
  const result = [];

  for (let i = 0; i < arr.length; i++) {
    result.push(arr[i]);

    // Check the gap between current and next number
    if (i < arr.length - 1) {
      const currentNumber = arr[i].number;
      const nextNumber = arr[i + 1].number;

      if (nextNumber - currentNumber > 1) {
        result.push(
            {
              type: 'separator',
              number: currentNumber + 0.5,
              label: '…',
              variant: 'ghost',
              disabled: true,
            },
        );
      }
    }
  }

  return result;
};

const pageButtons = computed(() => {
  // Insert the current page
  let buttons = [
    {
      type: 'page',
      number: props.currentPage,
      label: props.currentPage,
      variant: 'primary',
    },
  ];

  // Insert the previous page
  if (props.currentPage > 1) {
    buttons.push(
        {
          type: 'page',
          number: props.currentPage - 1,
          label: props.currentPage - 1,
        },
    );
  }

  // Insert the next page
  if (props.currentPage < props.totalPages) {
    buttons.push(
        {
          type: 'page',
          number: props.currentPage + 1,
          label: props.currentPage + 1,
        },
    );
  }

  // Insert the first page if it isn't there
  if (!buttons.some(el => el.number === 1)) {
    buttons.push(
        {
          type: 'page',
          number: 1,
          label: 1,
        },
    );
  }

  // Insert the last page if it isn't there
  if (!buttons.some(el => el.number === props.totalPages)) {
    buttons.push(
        {
          type: 'page',
          number: props.totalPages,
          label: props.totalPages,
        },
    );
  }

  // Sort
  buttons.sort((a, b) => a.number - b.number);
  buttons = insertEllipsis(buttons);

  return buttons;
});

const componentClasses = computed(() => [
  'stk-pagination',
  props.size ? `stk-size-${props.size}` : '',
])
</script>

<style>
.stk-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>